<script setup lang="ts">
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Loader2, X, FileUp, Asterisk } from 'lucide-vue-next';
import { FormField, FormItem, FormControl, FormMessage, FormLabel } from '@/components/ui/form';
import { useFileUpload } from '@/components/upload-file/useFileUpload';

const props = defineProps<{
    label: string;
    required?: boolean;
    fileUpload: ReturnType<typeof useFileUpload>;
}>();

const { files, fileLoader, uploadFile, removeFile } = props.fileUpload;
</script>

<template>
    <div class="grid gap-3 overflow-hidden">
        <Label class="font-semibold flex align-middle">
            {{ label }}
            <Asterisk v-if="required" class="text-red-600 size-4 inline-block" />
            <span v-else class="text-gray-400 text-md font-medium ml-1">(optional)</span>
        </Label>

        <FormField name="file">
            <FormItem>
                <FormLabel
                        class="flex justify-self-start border border-gray-300 bg-white py-2 px-5 rounded-md text-gray-950 font-bold text-md min-h-9 active:scale-95 transition-all"
                        :class="{ 'bg-gray-100 cursor-not-allowed': fileLoader, 'cursor-pointer': !fileLoader }">
                    
                        <div v-if="fileLoader" class="flex justify-center items-center h-full">
                            <Loader2 class="animate-spin size-5 mr-2" />
                            Uploading...
                        </div>
                        <div v-else class="flex justify-center items-center h-full">
                            <FileUp class="size-5 mr-2" />
                            Upload
                        </div>
                </FormLabel>
                <FormControl>
                    <Input type="file" multiple class="hidden" accept=".jpg, .jpeg, .pdf, .png"
                        @change="uploadFile" />
                </FormControl>
                <FormMessage name="uploadedFiles" />
            </FormItem>
        </FormField>

        <div v-if="files.length" class="flex gap-5 flex-wrap mt-4">
            <div v-for="(file, index) in files" :key="file.value.id" class="flex items-center border rounded-full overflow-hidden !bg-white">
                <span class="px-2 py-2 pl-3.5 text-gray-800 font-medium text-sm">
                    {{ file.value.name }}
                </span>
                <div @click="removeFile(index)"
                    class="flex items-center rounded-none border-l px-2 h-full cursor-pointer">
                    <X class="w-4 h-4 text-gray-500" />
                </div>
            </div>
        </div>
    </div>
</template>
